import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Shield, Clock, Check, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [reservation, setReservation] = useState(null);
  const [circuit, setCircuit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState("full"); // "full" or "deposit"
  const DEPOSIT_PERCENTAGE = 30;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resResponse = await axios.get(`${API}/reservations/${reservationId}`);
        setReservation(resResponse.data);

        const circuitResponse = await axios.get(`${API}/circuits/${resResponse.data.circuit_id}`);
        setCircuit(circuitResponse.data);
      } catch (error) {
        console.error("Error fetching reservation:", error);
        toast.error(t("common.error"));
        navigate("/circuits");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reservationId, navigate, t]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/reservations/${reservationId}/checkout`,
        { payment_type: paymentType },
        {
          headers: {
            origin: window.location.origin,
          },
        }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error(t("booking.paymentError"));
    } finally {
      setProcessing(false);
    }
  };

  const depositAmount = reservation ? (reservation.total_price * DEPOSIT_PERCENTAGE / 100) : 0;
  const amountToPay = paymentType === "deposit" ? depositAmount : (reservation?.total_price || 0);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!reservation || !circuit) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">{t("common.error")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-muted" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-3xl md:text-4xl font-bold">
            {t("booking.title")}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-card rounded-2xl p-6 shadow-lg border border-border h-fit">
            <h2 className="font-serif text-xl font-semibold mb-6">
              {lang === "fr" ? "Récapitulatif" : "Summary"}
            </h2>

            {/* Circuit Info */}
            <div className="flex gap-4 mb-6 pb-6 border-b border-border">
              <img
                src={circuit.image_url}
                alt={circuit.title[lang] || circuit.title.fr}
                className="w-24 h-24 rounded-xl object-cover"
              />
              <div>
                <h3 className="font-semibold text-lg">
                  {circuit.title[lang] || circuit.title.fr}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {circuit.duration_days} {t("circuits.duration")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("circuits.departure")}: {circuit.departure_city}
                </p>
              </div>
            </div>

            {/* Reservation Details */}
            <div className="space-y-3 mb-6 pb-6 border-b border-border">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("contact.name")}</span>
                <span className="font-medium">{reservation.user_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("booking.date")}</span>
                <span className="font-medium">{reservation.preferred_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("booking.participants")}</span>
                <span className="font-medium">{reservation.num_travelers}</span>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  €{circuit.price} x {reservation.num_travelers} {t("circuits.persons")}
                </span>
                <span>€{(circuit.price * reservation.num_travelers).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-3 border-t border-border">
                <span>{t("booking.total")}</span>
                <span className="text-primary">€{reservation.total_price.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="space-y-6">
            <div className="bg-card rounded-2xl p-6 shadow-lg border border-border">
              <h2 className="font-serif text-xl font-semibold mb-6">
                {lang === "fr" ? "Paiement sécurisé" : "Secure Payment"}
              </h2>

              {/* Payment Type Selection */}
              <div className="mb-6 p-4 bg-muted/50 rounded-xl">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Percent className="h-4 w-4 text-primary" />
                  {lang === "fr" ? "Choisissez votre mode de paiement" : "Choose your payment option"}
                </h3>
                <RadioGroup 
                  value={paymentType} 
                  onValueChange={setPaymentType}
                  className="space-y-3"
                >
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentType === "full" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value="full" id="full" />
                    <Label htmlFor="full" className="flex-1 cursor-pointer">
                      <div className="font-medium">
                        {lang === "fr" ? "Paiement intégral" : "Full payment"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {lang === "fr" 
                          ? `Payez la totalité maintenant : €${reservation?.total_price?.toFixed(2)}`
                          : `Pay the full amount now: €${reservation?.total_price?.toFixed(2)}`
                        }
                      </div>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentType === "deposit" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value="deposit" id="deposit" />
                    <Label htmlFor="deposit" className="flex-1 cursor-pointer">
                      <div className="font-medium flex items-center gap-2">
                        {lang === "fr" ? "Acompte de 30%" : "30% Deposit"}
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          {lang === "fr" ? "Recommandé" : "Recommended"}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {lang === "fr" 
                          ? `Payez €${depositAmount.toFixed(2)} maintenant, le reste (€${(reservation?.total_price - depositAmount).toFixed(2)}) sur place`
                          : `Pay €${depositAmount.toFixed(2)} now, the rest (€${(reservation?.total_price - depositAmount).toFixed(2)}) on arrival`
                        }
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Amount to Pay */}
              <div className="mb-6 p-4 bg-primary/10 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {lang === "fr" ? "Montant à payer maintenant" : "Amount to pay now"}
                  </span>
                  <span className="text-2xl font-bold text-primary">€{amountToPay.toFixed(2)}</span>
                </div>
                {paymentType === "deposit" && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {lang === "fr" 
                      ? `Solde restant à régler sur place : €${(reservation?.total_price - depositAmount).toFixed(2)}`
                      : `Remaining balance to pay on arrival: €${(reservation?.total_price - depositAmount).toFixed(2)}`
                    }
                  </p>
                )}
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-muted rounded-xl">
                  <Shield className="h-6 w-6 mx-auto text-green-600 mb-2" />
                  <span className="text-xs text-muted-foreground">
                    {lang === "fr" ? "Paiement sécurisé" : "Secure"}
                  </span>
                </div>
                <div className="text-center p-4 bg-muted rounded-xl">
                  <CreditCard className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                  <span className="text-xs text-muted-foreground">Stripe</span>
                </div>
                <div className="text-center p-4 bg-muted rounded-xl">
                  <Clock className="h-6 w-6 mx-auto text-primary mb-2" />
                  <span className="text-xs text-muted-foreground">
                    {lang === "fr" ? "Confirmation immédiate" : "Instant"}
                  </span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                className="w-full rounded-full bg-primary hover:bg-primary/90 py-6 text-lg"
                disabled={processing}
                data-testid="pay-now-btn"
              >
                {processing 
                  ? t("booking.paymentPending") 
                  : `${lang === "fr" ? "Payer" : "Pay"} €${amountToPay.toFixed(2)}`
                }
                <CreditCard className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                {lang === "fr"
                  ? "Vous serez redirigé vers la page de paiement sécurisée Stripe"
                  : "You will be redirected to Stripe's secure payment page"}
              </p>
            </div>

            {/* What's included */}
            {circuit.includes && (
            <div className="bg-card rounded-2xl p-6 shadow-lg border border-border">
              <h3 className="font-semibold mb-4">{t("circuits.includes")}</h3>
              <ul className="space-y-2">
                {(circuit.includes[lang] || circuit.includes.fr || []).slice(0, 5).map((item, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
